import uniq from "lodash.uniq";
import uniqBy from "lodash.uniqby";
import { App, Zap } from "@zapier/client";
import { getGeneratedImageUrl } from "@zapier/utils";
import { StorageZapStep } from "@zapier/bff";

export type ZapLike = Pick<Zap, "currentVersion" | "draft">;

export interface Service {
  authenticationId?: number | null;
  app?: string | null;
  name: string;
  src: string;
}

export const getServicesFromZDLSteps = (
  steps: StorageZapStep[],
  services: Service[],
): Service[] => {
  steps
    // filter out steps that don't have apps associated
    .filter(({ app }) => !!app)
    .forEach((step, index) => {
      let name = step.app?.split("@")[0];
      const source = getGeneratedImageUrl(name);

      // Replace the image for EngineAPI to properly reflect Paths
      if (name === "BranchingAPI") {
        name = "Paths by Zapier";
      }

      // We should not expose EngineAPI as a service
      if (step.app !== "EngineAPI") {
        services.push({
          authenticationId: step.authenticationId,
          name: name || `Step ${index}`,
          app: step.app,
          src: source,
        });
      }

      // Recursively add all subservices to services list
      if (step.steps) {
        getServicesFromZDLSteps(step.steps, services);
      }
    });

  return services;
};

export const getZapServicesFromZDL = (zap: ZapLike): Service[] => {
  // Else, default to building from ZDL steps
  const steps = zap.currentVersion?.zdl?.steps || zap.draft?.zdl?.steps || [];

  //  Filter out all duplicate apps
  return uniqBy(getServicesFromZDLSteps(steps, [] as Service[]), "app");
};

export const getServiceNames = (
  services: Service[],
  apps?: Record<string, App>,
) => {
  const sanitized = services
    .map((service) => {
      if (service.app && apps?.[service.app]?.name) {
        // Special case for Paths
        if (service.name === "Paths by Zapier") {
          return service.name;
        }
        return apps[service.app].name as string;
      }
      return (
        (service.name || "")
          // Filter out all the noise from the service name
          .replace("API", "")
          .replace("CLI", "")
          .replace("V1", "")
          .replace("V2", "")
          .trim()
      );
    })
    .filter((name) => !name.includes("Step ") && name);

  // Trim repeats if this is longer than 2 steps
  if (sanitized.length > 2) {
    return uniq(sanitized).join(", ").trim();
  }

  return sanitized.join(", ").trim();
};
